/**
 * Session Storage avec obfuscation simple
 * Utilisé pour stocker la clé WA API de manière temporaire (durée de session)
 */
import debug from '@commons-shared/debug.js';
import { config } from '../config/environment.js';

/**
 * Obfuscation simple par XOR
 */
class SimpleObfuscation {
  constructor(secretKey = 'wa-bellow-session-key') {
    this.secretKey = secretKey;
  }

  obfuscate(text) {
    if (!text) return '';
    
    try {
      const obfuscated = Array.from(text)
        .map((char, i) => 
          String.fromCharCode(
            char.charCodeAt(0) ^ this.secretKey.charCodeAt(i % this.secretKey.length)
          )
        )
        .join('');
      
      return btoa(obfuscated);
    } catch (error) {
      debug.error(config, 'Obfuscation failed:', error);
      return text; // Fallback to plain text
    }
  }

  deobfuscate(obfuscatedText) {
    if (!obfuscatedText) return '';
    
    try {
      const decoded = atob(obfuscatedText);
      const original = Array.from(decoded)
        .map((char, i) => 
          String.fromCharCode(
            char.charCodeAt(0) ^ this.secretKey.charCodeAt(i % this.secretKey.length)
          )
        )
        .join('');
      
      return original;
    } catch (error) {
      debug.error(config, 'Deobfuscation failed:', error);
      return obfuscatedText; // Fallback to original text
    }
  }
}

/**
 * Gestionnaire de stockage en session avec obfuscation
 */
class SessionStorage {
  constructor() {
    this.obfuscator = new SimpleObfuscation();
  }

  /**
   * Stocke une valeur obfusquée dans sessionStorage
   * @param {string} key - Clé de stockage
   * @param {string} value - Valeur à stocker
   * @param {boolean} obfuscate - Si true, obfusque la valeur (par défaut: true)
   */
  setItem(key, value, obfuscate = true) {
    if (!value) {
      this.removeItem(key);
      return;
    }

    try {
      const dataToStore = {
        value: obfuscate ? this.obfuscator.obfuscate(value) : value,
        obfuscated: obfuscate,
        timestamp: Date.now()
      };

      sessionStorage.setItem(key, JSON.stringify(dataToStore));
      debug.log(config, `SessionStorage: Stored ${key} (obfuscated: ${obfuscate})`);
    } catch (error) {
      debug.error(config, `SessionStorage: Failed to store ${key}:`, error);
    }
  }

  /**
   * Récupère une valeur depuis sessionStorage
   * @param {string} key - Clé de stockage
   * @returns {string|null} - Valeur déobfusquée ou null
   */
  getItem(key) {
    try {
      const item = sessionStorage.getItem(key);
      if (!item) return null;

      const data = JSON.parse(item);
      
      // Déobfusquer si nécessaire
      if (data.obfuscated) {
        return this.obfuscator.deobfuscate(data.value);
      }
      
      return data.value;
    } catch (error) {
      debug.error(config, `SessionStorage: Failed to retrieve ${key}:`, error);
      return null;
    }
  }

  /**
   * Supprime une valeur de sessionStorage
   * @param {string} key - Clé de stockage
   */
  removeItem(key) {
    try {
      sessionStorage.removeItem(key);
      debug.log(config, `SessionStorage: Removed ${key}`);
    } catch (error) {
      debug.error(config, `SessionStorage: Failed to remove ${key}:`, error);
    }
  }

  /**
   * Vérifie si une clé existe
   * @param {string} key - Clé de stockage
   * @returns {boolean}
   */
  hasItem(key) {
    return this.getItem(key) !== null;
  }

  /**
   * Efface tout le sessionStorage
   */
  clear() {
    try {
      sessionStorage.clear();
      debug.log(config, 'SessionStorage: Cleared all items');
    } catch (error) {
      debug.error(config, 'SessionStorage: Failed to clear:', error);
    }
  }
}

// Instance singleton
export const sessionStorageManager = new SessionStorage();

/**
 * Gestionnaire spécialisé pour la clé WA API
 */
export const waSessionStorage = {
  /**
   * Stocke la clé WA API obfusquée dans sessionStorage
   * @param {string} apiKey - Clé API WA
   */
  setWAApiKey(apiKey) {
    sessionStorageManager.setItem('wa_api_key', apiKey, true);
  },

  /**
   * Récupère la clé WA API depuis sessionStorage
   * @returns {string|null} - Clé API déobfusquée ou null
   */
  getWAApiKey() {
    return sessionStorageManager.getItem('wa_api_key');
  },

  /**
   * Supprime la clé WA API de sessionStorage
   */
  removeWAApiKey() {
    sessionStorageManager.removeItem('wa_api_key');
  },

  /**
   * Vérifie si une clé WA API valide existe
   * @returns {boolean}
   */
  hasValidWAApiKey() {
    const key = this.getWAApiKey();
    return !!(key && key.length > 0);
  }
};

export default sessionStorageManager;
